import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  styled,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import { USER_URLS } from '../config/urls';

// Styled components
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  boxShadow: 'none',
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none'
    }
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    }
  }
}));

const PasswordPage = () => {
  // State declarations
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Password validation
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      current_password: '',
      new_password: '',
      confirm_password: ''
    };

    if (!passwords.current_password) {
      newErrors.current_password = 'Current password is required';
      valid = false;
    }

    if (!passwords.new_password) {
      newErrors.new_password = 'New password is required';
      valid = false;
    } else if (passwords.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long';
      valid = false;
    }

    if (passwords.new_password !== passwords.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Password update handler
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        USER_URLS.updatePassword,
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setSuccess(true);
      setPasswords({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Current password is incorrect. Please try again.');
      } else {
        setError('Failed to update password. Please try again.');
      }
      console.error('Error updating password:', err);
    } finally {
      setLoading(false);
    }
  };

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Toggle password visibility
  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Close snackbar handler
  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 500, 
              mb: 1,
              fontSize: '1.5rem'
            }}>
              Password Settings
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}>
              Update your password to keep your account secure
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          height: '2px', 
          bgcolor: '#F2F2F2', 
          width: '78%'
        }} />
      </Box>

      {/* Password Form */}
      <form onSubmit={handlePasswordUpdate}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
            Change Password
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Current Password"
                name="current_password"
                type={showPassword.current ? 'text' : 'password'}
                value={passwords.current_password}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                error={Boolean(errors.current_password)}
                helperText={errors.current_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleTogglePassword('current')}
                        edge="end"
                      >
                        {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="New Password"
                name="new_password"
                type={showPassword.new ? 'text' : 'password'}
                value={passwords.new_password}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                error={Boolean(errors.new_password)}
                helperText={errors.new_password || 'Use 8 or more characters with a mix of letters, numbers & symbols'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleTogglePassword('new')}
                        edge="end"
                      >
                        {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Confirm New Password"
                name="confirm_password"
                type={showPassword.confirm ? 'text' : 'password'}
                value={passwords.confirm_password}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                error={Boolean(errors.confirm_password)}
                helperText={errors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleTogglePassword('confirm')}
                        edge="end"
                      >
                        {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <StyledButton
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Password'}
          </StyledButton>
        </Box>
      </form>

      {/* Success/Error messages */}
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Password updated successfully!
        </Alert>
      </Snackbar>
      
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PasswordPage;